import { Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import { SampleScope, seed } from '../../../../components/FrostedGate/SampleScope';
import { PageContainer } from '../../../../components/Layout/PageContainer';
import { getRouteTitle, ROUTES } from '../../../../constants/RouteNameMapping';
import { KPIRequestStatus } from '../../../../data-models/kpi-requests.data-model';
import { kpisRequestsListStateJ } from '../../../../services/state/KPI/KPIRequestsStateJ';
import { PREVIEW_LEGACY_REQUESTS } from '../../kpi-module.fixtures';
import { kpiBaseSeeds } from '../../kpi-sample-seeds';
import { useSampleKpiCompanies } from '../../useSampleKpiCompanies';
import { KPIRequestsTabs } from './KPIRequestsTabs';
import { KPIResponsesGrid } from './KPIResponsesGrid';
import KPIResponsesTabs from './KPIResponsesTabs';
import { SentRequests } from './SentRequests';
import { UnsentKpiRequestsGrid } from './UnsentKpiRequestsGrid';

type QueueKind = 'requests' | 'responses';

const RESPONSE_STATUS_BY_TAB: Record<string, KPIRequestStatus> = {
  [ROUTES.KPI_RESPONSES_ACCEPTED]: KPIRequestStatus.Accepted,
  [ROUTES.KPI_RESPONSES_REJECTED]: KPIRequestStatus.Rejected,
  [ROUTES.KPI_RESPONSES_MS_PENDING]: KPIRequestStatus.ManagedServiceSubmitted,
  [ROUTES.KPI_RESPONSES_MS_IN_PROGRESS]: KPIRequestStatus.ManagedServiceInProgress,
  [ROUTES.KPI_RESPONSES_MS_REVIEWED]: KPIRequestStatus.ManagedServiceFinished,
};

interface KpiRequestQueuePreviewProps {
  kind: QueueKind;
  tab?: string;
}

function QueueBody({ kind, tab }: KpiRequestQueuePreviewProps) {
  if (kind === 'requests') {
    if (tab === ROUTES.KPI_REQUESTS_IN_PROGRESS) {
      return <SentRequests status={KPIRequestStatus.Sent} />;
    }
    if (tab === ROUTES.KPI_REQUESTS_ARCHIVED) {
      return <SentRequests status={KPIRequestStatus.Archived} />;
    }
    return <UnsentKpiRequestsGrid />;
  }
  return (
    <KPIResponsesGrid
      status={(tab ? RESPONSE_STATUS_BY_TAB[tab] : undefined) ?? KPIRequestStatus.Submitted}
    />
  );
}

/** The live legacy Requests and Responses queues over a sample queue, while the module is frosted. */
export const KpiRequestQueuePreview: FC<KpiRequestQueuePreviewProps> = ({ kind, tab }) => {
  const companies = useSampleKpiCompanies();
  const seeds = useMemo(
    () => [...kpiBaseSeeds(companies), seed(kpisRequestsListStateJ, PREVIEW_LEGACY_REQUESTS)],
    [companies]
  );

  return (
    <SampleScope seeds={seeds}>
      <PageContainer>
        <Typography variant='h4'>
          {getRouteTitle(kind === 'requests' ? ROUTES.KPI_REQUESTS : ROUTES.KPI_RESPONSES)}
        </Typography>
        {kind === 'requests' ? <KPIRequestsTabs /> : <KPIResponsesTabs />}
        <QueueBody kind={kind} tab={tab} />
      </PageContainer>
    </SampleScope>
  );
};
